import { FiType } from 'react-icons/fi';

export const individualWordsData = {
  courseId: 'individual-words',
  courseType: 'vocabulary',
  title: 'Individual Word Study',
  icon: FiType,
  overview: {
    impact: 'Master tricky vocabulary',
    focus: 'Commonly confused and misused words',
    uses: 'All IELTS sections',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Commonly Confused Words',
      content: `<p>These word pairs often cause errors:</p>`,
      table: {
        headers: ['Words', 'Difference'],
        rows: [
          ['affect / effect', 'Affect = verb (to influence), Effect = noun (result)'],
          ['principal / principle', 'Principal = main/head, Principle = rule/belief'],
          ['compliment / complement', 'Compliment = praise, Complement = complete/match'],
          ['stationary / stationery', 'Stationary = not moving, Stationery = paper/pens'],
          ['accept / except', 'Accept = receive, Except = excluding'],
        ],
      },
    },
    {
      title: 'Formal vs Informal',
      content: `<p>Use formal vocabulary in IELTS Writing:</p>`,
      table: {
        headers: ['Informal', 'Formal'],
        rows: [
          ['lots of', 'numerous, a significant number of'],
          ['get', 'obtain, acquire, receive'],
          ['big', 'substantial, considerable, significant'],
          ['good', 'beneficial, advantageous, positive'],
          ['bad', 'detrimental, harmful, negative'],
        ],
      },
    },
    {
      title: 'Word Formation',
      content: `<p>Learn word families to expand your vocabulary quickly:</p>`,
      examples: [
        { label: 'Base', text: 'economy' },
        { label: 'Adjective', text: 'economic, economical' },
        { label: 'Adverb', text: 'economically' },
        { label: 'Verb', text: 'economize' },
        { label: 'Noun (person)', text: 'economist' },
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Word',
      type: 'select',
      instructions: 'Select the correct word for each sentence.',
      questions: [
        {
          text: 'Climate change will ___ millions of people.',
          options: ['affect', 'effect'],
          answer: 'affect',
          explanation: '"Affect" is the verb meaning to influence.',
        },
        {
          text: 'The ___ cause of the problem is pollution.',
          options: ['principal', 'principle'],
          answer: 'principal',
          explanation: '"Principal" means main or primary.',
        },
        {
          text: 'Everyone attended ___ the manager.',
          options: ['except', 'accept'],
          answer: 'except',
          explanation: '"Except" means excluding.',
        },
        {
          text: 'The policy had a negative ___ on the economy.',
          options: ['effect', 'affect'],
          answer: 'effect',
          explanation: '"Effect" is the noun meaning result or impact.',
        },
        {
          text: 'It is against my ___ to lie.',
          options: ['principles', 'principals'],
          answer: 'principles',
          explanation: '"Principles" = beliefs or rules of conduct.',
        },
        {
          text: 'I will ___ your invitation.',
          options: ['accept', 'except'],
          answer: 'accept',
          explanation: '"Accept" means to receive or agree to something.',
        },
      ],
    },
    {
      title: 'Formal or Informal?',
      type: 'select',
      instructions: 'Choose the more formal word for IELTS Writing.',
      questions: [
        {
          text: 'Replace "lots of":',
          options: ['numerous', 'heaps of', 'tons of'],
          answer: 'numerous',
          explanation: '"Numerous" is the formal alternative.',
        },
        {
          text: 'Replace "get":',
          options: ['obtain', 'grab', 'snag'],
          answer: 'obtain',
          explanation: '"Obtain" is formal and appropriate for academic writing.',
        },
        {
          text: 'Replace "big":',
          options: ['substantial', 'massive', 'huge'],
          answer: 'substantial',
          explanation: '"Substantial" is more academic than "massive" or "huge".',
        },
        {
          text: 'Replace "bad":',
          options: ['detrimental', 'terrible', 'awful'],
          answer: 'detrimental',
          explanation: '"Detrimental" is the most formal option.',
        },
      ],
    },
    {
      title: 'Complete the Word Family',
      type: 'fill-in',
      instructions: 'Type the correct word form.',
      questions: [
        {
          text: 'Base: develop → Noun: ___',
          answer: 'development',
          explanation: 'Development is the noun form of develop.',
        },
        {
          text: 'Base: economy → Adverb: ___',
          answer: 'economically',
          explanation: 'Economically is the adverb form.',
        },
        {
          text: 'Base: create → Adjective: ___',
          answer: 'creative',
          explanation: 'Creative is the adjective form of create.',
        },
        {
          text: 'Base: success → Adjective: ___',
          answer: 'successful',
          explanation: 'Successful is the adjective form of success.',
        },
      ],
    },
    {
      title: 'Choose Correct Word Form',
      type: 'select',
      instructions: 'Select the correct word form for each sentence.',
      questions: [
        {
          text: 'The ___ of the project was remarkable.',
          options: ['success', 'successful', 'successfully'],
          answer: 'success',
          explanation: 'We need a noun after "the".',
        },
        {
          text: 'The economy is growing ___.',
          options: ['rapidly', 'rapid', 'rapidity'],
          answer: 'rapidly',
          explanation: 'We need an adverb to modify the verb "growing".',
        },
        {
          text: 'This is an ___ solution.',
          options: ['effective', 'effect', 'effectively'],
          answer: 'effective',
          explanation: 'We need an adjective before the noun "solution".',
        },
      ],
    },
    {
      title: 'Upgrade to Formal Vocabulary',
      type: 'writing',
      instructions: 'Rewrite each sentence using more formal vocabulary.',
      questions: [
        {
          text: '"Lots of people think technology is good."',
          example: 'Numerous individuals believe technology is beneficial.',
          tip: 'lots of → numerous, people → individuals, think → believe, good → beneficial',
        },
        {
          text: '"We need to get better results."',
          example: 'We need to obtain improved outcomes.',
          tip: 'get → obtain, better → improved, results → outcomes',
        },
        {
          text: '"This has a bad effect on kids."',
          example: 'This has a detrimental impact on children.',
          tip: 'bad → detrimental, effect → impact, kids → children',
        },
      ],
    },
  ],
  tips: [
    'Create flashcards for confusing word pairs',
    'Use words in context to remember meanings',
    'Learn word families, not just individual words',
    'Replace informal words with formal alternatives',
  ],
  dailyPractice: [
    'Study one word family per day',
    'Practice affect/effect and other confusing pairs',
    'Upgrade informal vocabulary in your writing',
    'Read academic texts to see formal vocabulary',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Individual Word Study: Commonly confused words, formal vocabulary alternatives, and word formation for IELTS success.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...individualWordsData,
      title: individualWordsData.title + ' (Foundations)',
      overview: {
        ...individualWordsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: individualWordsData.sections.slice(0, Math.max(2, Math.ceil(individualWordsData.sections.length / 2))),
      exercises: individualWordsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...individualWordsData,
      title: individualWordsData.title + ' (Advanced)',
      overview: {
        ...individualWordsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: individualWordsData.sections,
      exercises: individualWordsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(individualWordsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return individualWordsData;
}
